/**
 * AiAgentFormDialog — iter-218t (Feb 2026)
 *
 * Create/edit dialog for a single entry in the central AI Agent Registry
 * (backend `admin_ai_registry.py`, collection `ai_agent_registry`).
 *
 * The dialog auto-fetches:
 *   - meta/platforms  → dropdown for platform_code (only non-future ones enabled)
 *   - meta/providers  → dropdown for provider_code (Gemini / Claude / OpenAI)
 *                       + model list per provider
 *   - meta/next-code  → live preview of the exact code that will be assigned
 *                       on save, e.g. "ASB-AI-GEM-CGB-004"
 *
 * On edit mode (agentCode prop set) it hydrates from GET /admin/ai-registry/{code}
 * and disables the provider/platform pickers (immutable — changing them would
 * break every reference to the agent).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, Save, X, Wand2, Info } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Input }  from '../../components/ui/input';
import { getAuthHeaders } from '../../utils/authHeaders';
import { TranslateButton } from './TranslateButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function AiAgentFormDialog({ agentCode, onClose, onSaved, language }) {
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const isEdit = !!agentCode;

  const [platforms, setPlatforms] = useState([]);
  const [providers, setProviders] = useState([]);
  const [nextCode,  setNextCode]  = useState('');

  const [form, setForm] = useState({
    provider_code:  'GEM',
    platform_code:  'ASB',
    name_ar:        '',
    name_en:        '',
    // iter-233 — Public persona name shown to end-users of the caller
    // platform. Editable both by admin here and by that platform's own
    // Emergent agent via /api/ai-core/bots/{code}/config.
    display_name_ar: '',
    display_name_en: '',
    description_ar: '',
    description_en: '',
    model_used:     '',
    system_prompt:  '',
    // iter-218u — Bilingual capability tags shown as chips in the directory.
    // Stored as arrays server-side, edited here as comma-separated strings
    // for simplicity (matches how admins naturally write tag lists).
    capabilities_ar_str: '',
    capabilities_en_str: '',
    // iter-299e (Feb 2026) — Draft state removed from the UX.
    // New bots default to 'active'; the admin can flip to 'deprecated'
    // (shown as "متوقف / Stopped") from the form or from the detail
    // page's Pause button.
    status:         'active',
    // iter-232c — Owner-agent concept removed. Bots are shared across
    // every user of the platform indicated by their code.
    rate_limit_enabled: true,
    monthly_call_limit: 500,
    paused: false,
    // iter-299l (Feb 2026) — Voice I/O. Every bot supports voice by default.
    voice_input_enabled:  true,
    voice_output_enabled: true,
    voice_name:           'Puck',
    // iter-299u — reasoning effort (Gemini 3.x). 'low' = fastest customer replies.
    thinking_level:       'minimal',
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving,  setSaving]  = useState(false);

  // Load meta on mount
  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/ai-registry/meta/platforms`, { headers: getAuthHeaders() }),
          axios.get(`${BACKEND_URL}/api/admin/ai-registry/meta/providers`, { headers: getAuthHeaders() }),
        ]);
        setPlatforms(p.data?.data || []);
        setProviders(m.data?.data || []);
      } catch (e) {
        toast.error(t('فشل تحميل بيانات القوائم', 'Failed to load dropdown data'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate on edit
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/admin/ai-registry/${agentCode}`,
          { headers: getAuthHeaders() },
        );
        const d = r.data?.data || {};
        setForm({
          provider_code:  d.provider_code,
          platform_code:  d.platform_code,
          name_ar:        d.name_ar        || '',
          name_en:        d.name_en        || '',
          display_name_ar: d.display_name_ar || '',
          display_name_en: d.display_name_en || '',
          description_ar: d.description_ar || '',
          description_en: d.description_en || '',
          model_used:     d.model_used     || '',
          system_prompt:  d.system_prompt  || '',
          capabilities_ar_str: (d.capabilities_ar || []).join(', '),
          capabilities_en_str: (d.capabilities_en || []).join(', '),
          // iter-299e — Draft is retired. Legacy drafts auto-promote
          // to 'active' in the form so a plain Save migrates them.
          status:         (d.status === 'draft' ? 'active' : (d.status || 'active')),
          rate_limit_enabled: d.rate_limit_enabled !== false,
          monthly_call_limit: Number.isFinite(d.monthly_call_limit) ? d.monthly_call_limit : 500,
          paused:             !!d.paused,
          // iter-299l — Voice I/O
          voice_input_enabled:  d.voice_input_enabled  !== false,
          voice_output_enabled: d.voice_output_enabled !== false,
          voice_name:           d.voice_name || 'Puck',
          thinking_level:       d.thinking_level || 'minimal',
        });
      } catch (e) {
        toast.error(t('فشل تحميل النموذج', 'Failed to load the agent'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentCode]);

  // Peek next-code whenever provider/platform changes (create mode only)
  useEffect(() => {
    if (isEdit) return;
    if (!form.provider_code || !form.platform_code) return;
    (async () => {
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/admin/ai-registry/meta/next-code?provider_code=${form.provider_code}&platform_code=${form.platform_code}`,
          { headers: getAuthHeaders() },
        );
        setNextCode(r.data?.data?.next_code || '');
      } catch { /* silent */ }
    })();
  }, [form.provider_code, form.platform_code, isEdit]);

  // Reset model_used when provider changes so we don't send an invalid combo
  useEffect(() => {
    if (isEdit) return;
    const prov = providers.find(p => p.code === form.provider_code);
    if (prov && prov.models?.length && !prov.models.includes(form.model_used)) {
      setForm(f => ({ ...f, model_used: prov.recommended_model || prov.models[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.provider_code, providers]);

  const currentProvider = useMemo(
    () => providers.find(p => p.code === form.provider_code),
    [providers, form.provider_code],
  );

  const speedLabel = (sp) => t({ fastest: 'الأسرع', fast: 'سريع', slow: 'بطيء (Pro)' }[sp] || '', { fastest: 'fastest', fast: 'fast', slow: 'slow (Pro)' }[sp] || '');
  const modelGroups = useMemo(() => {
    const meta = currentProvider?.models_meta || [];
    if (!meta.length) {
      return [{ label: t('النماذج', 'Models'), items: (currentProvider?.models || []).map(m => ({ id: m, label: m })) }];
    }
    const lbl = (m) => `${m.id}${m.recommended_for_chat ? '  ★' : ''}${m.stable ? '' : t('  (تجريبي)', '  (preview)')}`;
    const stable  = meta.filter(m => m.stable).map(m => ({ id: m.id, label: lbl(m) }));
    const preview = meta.filter(m => !m.stable).map(m => ({ id: m.id, label: lbl(m) }));
    const groups = [];
    if (stable.length)  groups.push({ label: t('مستقر — موصى به', 'Stable — recommended'), items: stable });
    if (preview.length) groups.push({ label: t('تجريبي (Preview)', 'Preview'), items: preview });
    return groups;
  }, [currentProvider, t]);
  const selectedModelMeta = useMemo(
    () => (currentProvider?.models_meta || []).find(m => m.id === form.model_used) || null,
    [currentProvider, form.model_used],
  );

  const submit = async () => {
    if (!form.name_ar.trim() || !form.name_en.trim()) {
      toast.error(t('املأ الاسم بالعربي والإنجليزي', 'Fill both AR and EN names'));
      return;
    }
    if (!form.model_used) {
      toast.error(t('اختر النموذج المستخدم', 'Pick a model'));
      return;
    }
    setSaving(true);
    try {
      // iter-218u — Convert the comma-separated inputs into the array form
      // the backend expects. Empty entries are filtered server-side too.
      const capsAr = form.capabilities_ar_str.split(',').map(s => s.trim()).filter(Boolean);
      const capsEn = form.capabilities_en_str.split(',').map(s => s.trim()).filter(Boolean);
      const payload = {
        ...form,
        capabilities_ar: capsAr,
        capabilities_en: capsEn,
      };
      delete payload.capabilities_ar_str;
      delete payload.capabilities_en_str;

      if (isEdit) {
        // provider/platform are immutable — strip from update payload.
        const { provider_code, platform_code, ...updates } = payload;   // eslint-disable-line no-unused-vars
        await axios.put(
          `${BACKEND_URL}/api/admin/ai-registry/${agentCode}`,
          updates,
          { headers: getAuthHeaders() },
        );
        toast.success(t('✓ تم تحديث النموذج', '✓ Agent updated'));
      } else {
        const r = await axios.post(
          `${BACKEND_URL}/api/admin/ai-registry`,
          payload,
          { headers: getAuthHeaders() },
        );
        toast.success(t(`✓ تم إنشاء ${r.data?.data?.agent_code}`, `✓ Created ${r.data?.data?.agent_code}`));
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || t('فشل الحفظ', 'Save failed');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      onClick={onClose}
      data-testid="ai-agent-dialog-backdrop"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0f0f0f] border border-white/10 rounded-2xl shadow-2xl"
        data-testid="ai-agent-dialog"
      >
        <header className="sticky top-0 bg-[#0f0f0f]/95 backdrop-blur border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#d4af37]" />
            <h3 className="text-base font-bold text-white">
              {isEdit
                ? t(`تعديل النموذج: ${agentCode}`, `Edit agent: ${agentCode}`)
                : t('إضافة نموذج ذكاء اصطناعي', 'Add AI agent')}
            </h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="ai-agent-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="py-12 text-center text-white/60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : (
            <>
              {/* Code preview */}
              {!isEdit && nextCode && (
                <div className="p-3 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#d4af37] shrink-0" />
                  <div className="text-[13px]">
                    <span className="text-white/60">{t('الكود التلقائي: ', 'Auto-assigned code: ')}</span>
                    <span className="font-mono font-bold text-[#d4af37]">{nextCode}</span>
                  </div>
                </div>
              )}

              {/* Provider + Platform */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">
                    {t('مزود الذكاء الاصطناعي', 'AI Provider')}
                  </label>
                  <select
                    value={form.provider_code}
                    onChange={(e) => setForm(f => ({ ...f, provider_code: e.target.value }))}
                    disabled={isEdit}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-11 px-3 text-white text-sm disabled:opacity-50 [color-scheme:dark]"
                    data-testid="ai-agent-provider"
                  >
                    {providers.map(p => (
                      <option key={p.code} value={p.code} className="bg-[#0a0a0a] text-white">
                        {p.code} — {isAr ? p.name_ar : p.name_en}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">
                    {t('المنصة', 'Platform')}
                  </label>
                  <select
                    value={form.platform_code}
                    onChange={(e) => setForm(f => ({ ...f, platform_code: e.target.value }))}
                    disabled={isEdit}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-11 px-3 text-white text-sm disabled:opacity-50 [color-scheme:dark]"
                    data-testid="ai-agent-platform"
                  >
                    {platforms.filter(p => !p.future).map(p => (
                      <option key={p.code} value={p.code} className="bg-[#0a0a0a] text-white">
                        {p.code} — {isAr ? p.name_ar : p.name_en}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Names */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>{t('الاسم بالعربي', 'Arabic name')}</span>
                    <TranslateButton sourceText={form.name_ar} source="ar" target="en" isAr={isAr}
                      onTranslated={(txt) => setForm(f => ({ ...f, name_en: txt }))}
                      testid="ai-agent-name-translate-en" />
                  </label>
                  <Input
                    value={form.name_ar}
                    onChange={(e) => setForm(f => ({ ...f, name_ar: e.target.value }))}
                    dir="rtl"
                    className="bg-[#0a0a0a] border-white/10 text-white h-11"
                    placeholder={t('مثال: بوت جلب الشركات', 'e.g. Companies Fetcher Bot')}
                    data-testid="ai-agent-name-ar"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>{t('الاسم بالإنجليزي', 'English name')}</span>
                    <TranslateButton sourceText={form.name_en} source="en" target="ar" isAr={isAr}
                      onTranslated={(txt) => setForm(f => ({ ...f, name_ar: txt }))}
                      testid="ai-agent-name-translate-ar" />
                  </label>
                  <Input
                    value={form.name_en}
                    onChange={(e) => setForm(f => ({ ...f, name_en: e.target.value }))}
                    dir="ltr"
                    className="bg-[#0a0a0a] border-white/10 text-white h-11"
                    placeholder="e.g. Companies Fetcher Bot"
                    data-testid="ai-agent-name-en"
                  />
                </div>
              </div>

              {/* iter-233 — Public persona (display name shown to end-users).
                 Editable both by admin here and by other-platform agents via
                 the /api/ai-core/bots/{code}/config endpoint. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2 flex items-center gap-2 -mb-1">
                  <span className="text-xs font-semibold text-blue-300">
                    {t('الاسم العلني للبوت (المُستخدم في ردوده على العملاء)',
                       'Public persona name (used in end-user replies)')}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-200 border border-blue-500/30">
                    {t('قابل للتعديل من ايجنت المنصة', 'Editable by platform agent')}
                  </span>
                </div>
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>{t('الاسم العلني (عربي)', 'Public name (AR)')}</span>
                    <TranslateButton sourceText={form.display_name_ar} source="ar" target="en" isAr={isAr}
                      onTranslated={(txt) => setForm(f => ({ ...f, display_name_en: txt }))}
                      testid="ai-agent-display-name-translate-en" />
                  </label>
                  <Input
                    value={form.display_name_ar}
                    onChange={(e) => setForm(f => ({ ...f, display_name_ar: e.target.value }))}
                    dir="rtl"
                    className="bg-[#0a0a0a] border-blue-500/30 text-white h-11"
                    placeholder={t('مثال: مساعد كلاود جي إم للتجار', 'e.g. مساعد كلاود جي إم للتجار')}
                    data-testid="ai-agent-display-name-ar"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>{t('الاسم العلني (إنجليزي)', 'Public name (EN)')}</span>
                    <TranslateButton sourceText={form.display_name_en} source="en" target="ar" isAr={isAr}
                      onTranslated={(txt) => setForm(f => ({ ...f, display_name_ar: txt }))}
                      testid="ai-agent-display-name-translate-ar" />
                  </label>
                  <Input
                    value={form.display_name_en}
                    onChange={(e) => setForm(f => ({ ...f, display_name_en: e.target.value }))}
                    dir="ltr"
                    className="bg-[#0a0a0a] border-blue-500/30 text-white h-11"
                    placeholder="e.g. CloudGM Merchant Assistant"
                    data-testid="ai-agent-display-name-en"
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>{t('الوصف (عربي)', 'Description (AR)')}</span>
                    <TranslateButton sourceText={form.description_ar} source="ar" target="en" isAr={isAr}
                      onTranslated={(txt) => setForm(f => ({ ...f, description_en: txt }))}
                      testid="ai-agent-desc-translate-en" />
                  </label>
                  <textarea
                    value={form.description_ar}
                    onChange={(e) => setForm(f => ({ ...f, description_ar: e.target.value }))}
                    dir="rtl"
                    rows={2}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm"
                    data-testid="ai-agent-desc-ar"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>{t('الوصف (إنجليزي)', 'Description (EN)')}</span>
                    <TranslateButton sourceText={form.description_en} source="en" target="ar" isAr={isAr}
                      onTranslated={(txt) => setForm(f => ({ ...f, description_ar: txt }))}
                      testid="ai-agent-desc-translate-ar" />
                  </label>
                  <textarea
                    value={form.description_en}
                    onChange={(e) => setForm(f => ({ ...f, description_en: e.target.value }))}
                    dir="ltr"
                    rows={2}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm"
                    data-testid="ai-agent-desc-en"
                  />
                </div>
              </div>

              {/* Model + Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>{t('النموذج المستخدم', 'Model version')}</span>
                    {currentProvider?.source === 'google_live' && (
                      <span className="text-[10px] text-emerald-300" data-testid="ai-agent-model-live-badge">
                        {t(`● حيّ من Google — ${(currentProvider.models || []).length} نموذج`, `● live from Google — ${(currentProvider.models || []).length} models`)}
                      </span>
                    )}
                  </label>
                  <select
                    value={form.model_used}
                    onChange={(e) => setForm(f => ({ ...f, model_used: e.target.value }))}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-11 px-3 text-white text-sm [color-scheme:dark]"
                    data-testid="ai-agent-model"
                  >
                    {modelGroups.map(g => (
                      <optgroup key={g.label} label={g.label}>
                        {g.items.map(m => (
                          <option key={m.id} value={m.id} className="bg-[#0a0a0a] text-white">{m.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {selectedModelMeta && (
                    <p className={`mt-1 text-[10px] ${selectedModelMeta.stable ? 'text-white/40' : 'text-amber-300/80'}`} data-testid="ai-agent-model-hint">
                      {selectedModelMeta.stable
                        ? t(`مستقر · ${speedLabel(selectedModelMeta.speed)}`, `stable · ${speedLabel(selectedModelMeta.speed)}`)
                        : t('⚠ نسخة تجريبية (Preview) — قد تتوقف فجأة وأبطأ غالباً', '⚠ Preview build — may be withdrawn and is usually slower')}
                    </p>
                  )}
                </div>
                {form.provider_code === 'GEM' && (
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1.5">{t('مستوى التفكير (السرعة)', 'Thinking level (speed)')}</label>
                    <select
                      value={form.thinking_level}
                      onChange={(e) => setForm(f => ({ ...f, thinking_level: e.target.value }))}
                      className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-11 px-3 text-white text-sm [color-scheme:dark]"
                      data-testid="ai-agent-thinking-level"
                    >
                      <option value="minimal" className="bg-[#0a0a0a] text-white">{t('أدنى — الأسرع (موصى به لخدمة العملاء)', 'Minimal — fastest (recommended for support)')}</option>
                      <option value="low" className="bg-[#0a0a0a] text-white">{t('منخفض — سريع', 'Low — fast')}</option>
                      <option value="medium" className="bg-[#0a0a0a] text-white">{t('متوسط — متوازن', 'Medium — balanced')}</option>
                      <option value="high" className="bg-[#0a0a0a] text-white">{t('عالٍ — أبطأ (تحليل معقّد)', 'High — slowest (complex analysis)')}</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">{t('الحالة', 'Status')}</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-11 px-3 text-white text-sm [color-scheme:dark]"
                    data-testid="ai-agent-status"
                  >
                    <option value="active" className="bg-[#0a0a0a] text-white">{t('نشط', 'Active')}</option>
                    <option value="deprecated" className="bg-[#0a0a0a] text-white">{t('متوقف', 'Stopped')}</option>
                  </select>
                </div>
              </div>

              {/* System prompt */}
              <div>
                <label className="block text-[11px] text-white/50 mb-1.5">{t('التعليمات (System Prompt)', 'System prompt')}</label>
                <textarea
                  value={form.system_prompt}
                  onChange={(e) => setForm(f => ({ ...f, system_prompt: e.target.value }))}
                  rows={6}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm font-mono"
                  placeholder={t('اكتب تعليمات النموذج هنا — يمكن أن تكون طويلة…',
                                 'Write the model instructions here — can be long…')}
                  data-testid="ai-agent-prompt"
                />
              </div>

              {/* iter-218u — Capabilities (comma-separated tags) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>
                      {t('المميزات (عربي)', 'Capabilities (AR)')}
                      <span className="text-white/30 mx-1">·</span>
                      <span className="text-white/40">{t('افصل بفاصلة', 'comma-separated')}</span>
                    </span>
                    <TranslateButton sourceText={form.capabilities_ar_str} source="ar" target="en" isAr={isAr}
                      onTranslated={(txt) => setForm(f => ({ ...f, capabilities_en_str: txt }))}
                      testid="ai-agent-caps-translate-en" />
                  </label>
                  <textarea
                    value={form.capabilities_ar_str}
                    onChange={(e) => setForm(f => ({ ...f, capabilities_ar_str: e.target.value }))}
                    dir="rtl"
                    rows={2}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm"
                    placeholder={t('محادثة تفاعلية, دعم عملاء, إرشاد للخدمات', 'e.g. Interactive chat, Customer support')}
                    data-testid="ai-agent-caps-ar"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-between text-[11px] text-white/50 mb-1.5">
                    <span>
                      {t('المميزات (إنجليزي)', 'Capabilities (EN)')}
                      <span className="text-white/30 mx-1">·</span>
                      <span className="text-white/40">{t('افصل بفاصلة', 'comma-separated')}</span>
                    </span>
                    <TranslateButton sourceText={form.capabilities_en_str} source="en" target="ar" isAr={isAr}
                      onTranslated={(txt) => setForm(f => ({ ...f, capabilities_ar_str: txt }))}
                      testid="ai-agent-caps-translate-ar" />
                  </label>
                  <textarea
                    value={form.capabilities_en_str}
                    onChange={(e) => setForm(f => ({ ...f, capabilities_en_str: e.target.value }))}
                    dir="ltr"
                    rows={2}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-white text-sm"
                    placeholder="e.g. Interactive chat, Customer support"
                    data-testid="ai-agent-caps-en"
                  />
                </div>
              </div>

              {/* iter-232c — Controls (rate limit + pause). Ownership removed;
                 bots are shared across every user of the platform indicated
                 by their code. */}
              <div className="pt-4 border-t border-white/5 space-y-3">
                <h4 className="text-sm font-bold text-[#d4af37]">
                  {t('التحكم', 'Controls')}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2 p-3 rounded-xl bg-[#0a0a0a] border border-white/10 cursor-pointer" data-testid="ai-agent-rate-limit-toggle">
                    <input
                      type="checkbox"
                      checked={form.rate_limit_enabled}
                      onChange={(e) => setForm(f => ({ ...f, rate_limit_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-[#d4af37]"
                    />
                    <span className="text-xs text-white">
                      {t('تفعيل حد الاستخدام', 'Rate limiting enabled')}
                    </span>
                  </label>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1.5">
                      {t('الحد الشهري للاستدعاءات', 'Monthly call limit')}
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step={50}
                      value={form.monthly_call_limit}
                      onChange={(e) => setForm(f => ({ ...f, monthly_call_limit: Number(e.target.value) || 0 }))}
                      disabled={!form.rate_limit_enabled}
                      dir="ltr"
                      className="bg-[#0a0a0a] border-white/10 text-white rounded-xl disabled:opacity-50"
                      data-testid="ai-agent-monthly-limit"
                    />
                  </div>
                  <label className="flex items-center gap-2 p-3 rounded-xl bg-[#0a0a0a] border border-white/10 cursor-pointer" data-testid="ai-agent-pause-toggle">
                    <input
                      type="checkbox"
                      checked={form.paused}
                      onChange={(e) => setForm(f => ({ ...f, paused: e.target.checked }))}
                      className="w-4 h-4 accent-rose-500"
                    />
                    <span className="text-xs text-white">
                      {t('إيقاف البوت مؤقتاً', 'Pause bot')}
                    </span>
                  </label>
                </div>
              </div>

              {/* iter-299l — Voice I/O section. Every bot supports voice input
                 and voice output by default; admin can toggle each off or
                 swap the TTS voice from Gemini's native prebuilt voices. */}
              <div className="pt-4 border-t border-white/5 space-y-3">
                <h4 className="text-sm font-bold text-sky-400 flex items-center gap-2">
                  <span>🎙️</span>
                  {t('الصوت (دخول + خروج)', 'Voice I/O')}
                  <span className="text-[10px] font-normal text-white/40 ms-2">
                    {t('يعمل مع كل البوتات تلقائياً عبر Gemini',
                       'Enabled on every bot via Gemini native STT + TTS')}
                  </span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="flex items-center gap-2 p-3 rounded-xl bg-[#0a0a0a] border border-white/10 cursor-pointer" data-testid="ai-agent-voice-input-toggle">
                    <input
                      type="checkbox"
                      checked={form.voice_input_enabled}
                      onChange={(e) => setForm(f => ({ ...f, voice_input_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-sky-500"
                    />
                    <span className="text-xs text-white flex items-center gap-1.5">
                      🎤 {t('استقبال صوت المستخدم', 'Accept voice input')}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 p-3 rounded-xl bg-[#0a0a0a] border border-white/10 cursor-pointer" data-testid="ai-agent-voice-output-toggle">
                    <input
                      type="checkbox"
                      checked={form.voice_output_enabled}
                      onChange={(e) => setForm(f => ({ ...f, voice_output_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-sky-500"
                    />
                    <span className="text-xs text-white flex items-center gap-1.5">
                      🔊 {t('رد صوتي للمستخدم', 'Reply with voice')}
                    </span>
                  </label>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1.5">
                      {t('صوت الرد', 'Voice (TTS)')}
                    </label>
                    <select
                      value={form.voice_name}
                      onChange={(e) => setForm(f => ({ ...f, voice_name: e.target.value }))}
                      disabled={!form.voice_output_enabled}
                      className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-11 px-3 text-white text-sm [color-scheme:dark] disabled:opacity-50"
                      data-testid="ai-agent-voice-name"
                    >
                      <option value="Puck"   className="bg-[#0a0a0a] text-white">{t('Puck — شاب عربي طبيعي', 'Puck — Young friendly male')}</option>
                      <option value="Charon" className="bg-[#0a0a0a] text-white">{t('Charon — رجل رسمي', 'Charon — Formal male')}</option>
                      <option value="Kore"   className="bg-[#0a0a0a] text-white">{t('Kore — امرأة شابة', 'Kore — Young female')}</option>
                      <option value="Aoede"  className="bg-[#0a0a0a] text-white">{t('Aoede — امرأة ناضجة', 'Aoede — Mature female')}</option>
                      <option value="Fenrir" className="bg-[#0a0a0a] text-white">{t('Fenrir — صوت عميق', 'Fenrir — Deep male')}</option>
                      <option value="Leda"   className="bg-[#0a0a0a] text-white">{t('Leda — امرأة لطيفة', 'Leda — Gentle female')}</option>
                      <option value="Orus"   className="bg-[#0a0a0a] text-white">{t('Orus — صوت قيادي', 'Orus — Authoritative male')}</option>
                      <option value="Zephyr" className="bg-[#0a0a0a] text-white">{t('Zephyr — صوت دافئ', 'Zephyr — Warm male')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <footer className="sticky bottom-0 bg-[#0f0f0f]/95 backdrop-blur border-t border-white/10 px-5 py-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:bg-white/5">
            {t('إلغاء', 'Cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={saving || loading}
            className="bg-[#d4af37] hover:bg-[#c9a227] text-black h-10 px-5"
            data-testid="ai-agent-save-btn"
          >
            {saving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <><Save className="w-4 h-4 me-1.5" />{isEdit ? t('حفظ التغييرات', 'Save changes') : t('إنشاء النموذج', 'Create agent')}</>}
          </Button>
        </footer>
      </motion.div>
    </div>
  );
}
